/**
 * deliverableRepeats.js — repeating question groups in deliverable templates.
 *
 * A template section may declare `repeat: "<family>"` (e.g. "c" for campaign
 * blocks). Its questions whose `group` is `<family>1` are the PROTOTYPE
 * instance; every higher instance (c2, c3, ... unbounded) derives from it by
 * number substitution in id, group, and label. This is what makes campaigns
 * UNCAPPED (owner ruling 2026-08-19: "no reason to cap at 4") without storing
 * forty copies of the same eight questions in the template row.
 *
 * THREE consumers, ONE definition — this file is load-bearing for a security
 * boundary, so it must never be forked:
 *   - service-api routes/m5mDeliverable.js — filterToAudience() decides which
 *     payload keys each audience may write. A pattern here is what lets
 *     am_c7_name through the staff PUT while still refusing it from the
 *     public routes (audience is carried per prototype question, and 'am' is
 *     explicit-only: an unmarked question can never be smuggled into the
 *     staff-authored map).
 *   - client-saas deliverable-fill-route.js — expands sections so a campaign
 *     the author added past the prototype renders for the client.
 *   - client-saas deliverables-route.js — the authoring drawer's "+ Add
 *     Campaign N" blocks and the read-only review drawer.
 *
 * Templates WITHOUT `repeat` (advertising_plan v1/v2, any questionnaire-shaped
 * template) pass through every function unchanged — pinned in-flight
 * deliverables keep working exactly as authored.
 */

/** The repeat family key of a section, or null. */
export function repeatFamily(section) {
  return section && typeof section.repeat === 'string' && section.repeat ? section.repeat : null;
}

/** The prototype (instance 1) questions of a repeat section. */
export function prototypeQuestions(section) {
  const family = repeatFamily(section);
  if (!family) return [];
  return (section.questions || []).filter((q) => q.group === `${family}1`);
}

/**
 * Clone the prototype as instance `n`: `_c1_` id segment renumbered, group
 * renumbered, and the label's first standalone "1" renumbered ("Campaign 1:
 * Name" → "Campaign 3: Name"). `help` is deliberately untouched — it can
 * legitimately contain numbers ("8 to 12 recommended").
 */
export function instanceQuestions(protoQuestions, family, n) {
  return (protoQuestions || []).map((q) => ({
    ...q,
    id: q.id.replace(`_${family}1_`, `_${family}${n}_`),
    group: `${family}${n}`,
    label: String(q.label || '').replace(/\b1\b/, String(n)),
  }));
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** ^am_c(\d+)_name$ from the prototype id am_c1_name. */
export function instanceIdPattern(protoId, family) {
  return new RegExp(`^${escapeRe(protoId).replace(`_${family}1_`, `_${family}(\\d+)_`)}$`);
}

/**
 * The audience decision table for a whole template: exact ids plus one
 * pattern per prototype question of each repeat section. Audience follows
 * the write-filter rule everywhere: 'am' must be EXPLICIT, anything else is
 * client-fillable.
 */
export function questionAudienceMap(sections) {
  const exact = new Map();
  const patterns = [];
  for (const s of Array.isArray(sections) ? sections : []) {
    for (const q of Array.isArray(s.questions) ? s.questions : []) {
      if (q.id) exact.set(q.id, q.audience === 'am' ? 'am' : 'cl');
    }
    const family = repeatFamily(s);
    if (family) {
      for (const q of prototypeQuestions(s)) {
        if (q.id && q.id.includes(`_${family}1_`)) {
          patterns.push({ re: instanceIdPattern(q.id, family), audience: q.audience === 'am' ? 'am' : 'cl' });
        }
      }
    }
  }
  return { exact, patterns };
}

/** Which audience owns this id — exact match first, then repeat patterns; null = not in the template. */
export function audienceOf(id, map) {
  if (map.exact.has(id)) return map.exact.get(id);
  for (const p of map.patterns) {
    if (p.re.test(id)) return p.audience;
  }
  return null;
}

// A render-side sanity ceiling ONLY (the server never expands — it pattern-
// matches keys directly, so no payload can make it loop). Far above any real
// plan; keeps a corrupt value map from generating a million DOM nodes.
const MAX_RENDER_INSTANCES = 200;

/** Highest instance number present in the given value maps for this section's family (min 1). */
export function maxInstance(section, ...valueMaps) {
  const family = repeatFamily(section);
  if (!family) return 0;
  const res = prototypeQuestions(section)
    .filter((q) => q.id && q.id.includes(`_${family}1_`))
    .map((q) => instanceIdPattern(q.id, family));
  let max = 1;
  for (const m of valueMaps) {
    for (const k of Object.keys(m || {})) {
      for (const re of res) {
        const hit = k.match(re);
        if (hit) max = Math.max(max, parseInt(hit[1], 10));
      }
    }
  }
  return Math.min(max, MAX_RENDER_INSTANCES);
}

/**
 * Sections with every populated repeat instance materialized, for display:
 * instance 1..maxInstance(content/answers) of each repeat section, prototype
 * order preserved, non-repeat questions (the section's review fields) after —
 * the same order the explicit v1/v2 templates used. Non-repeat sections pass
 * through untouched.
 */
export function expandSections(sections, ...valueMaps) {
  return (Array.isArray(sections) ? sections : []).map((s) => {
    const family = repeatFamily(s);
    if (!family) return s;
    const proto = prototypeQuestions(s);
    if (!proto.length) return s;
    const rest = (s.questions || []).filter((q) => q.group !== `${family}1`);
    const count = maxInstance(s, ...valueMaps);
    const expanded = [];
    for (let n = 1; n <= count; n++) expanded.push(...instanceQuestions(proto, family, n));
    return { ...s, questions: [...expanded, ...rest] };
  });
}

export default {
  repeatFamily,
  prototypeQuestions,
  instanceQuestions,
  instanceIdPattern,
  questionAudienceMap,
  audienceOf,
  maxInstance,
  expandSections,
};
